import { z } from 'zod';
import { CursorQuery, Paginated } from './common/pagination.js';
import { StorageObjectKey } from './storage.js';
import { HobbyTimeZone, GeoRegion } from './hobbies.js';
export const HobbyVisibility = z.enum(['everyone', 'followers', 'only_me']);
export const HobbiesPreferences = z.object({
    showWorldPercent: z.boolean().default(true),
    bucketVisibilityDefault: HobbyVisibility.default('everyone'),
});
export const TripType = z.enum(['leisure', 'work', 'family', 'volunteering']);
export const TravelMedia = z.object({ key: StorageObjectKey, url: z.string().url() });
export const TripFields = z.object({
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    subdivisionCodes: z
        .array(z.string().min(2).max(20))
        .min(1)
        .max(500)
        .refine((items) => new Set(items).size === items.length, 'Choose each place once'),
    startDate: z.iso.date(),
    endDate: z.iso.date().nullable(),
    tripType: TripType,
    mediaIds: z
        .array(StorageObjectKey)
        .max(20)
        .refine((items) => new Set(items).size === items.length, 'Choose each photo once'),
    shareAsPost: z.boolean(),
    scope: z.enum(['local', 'state', 'national', 'world']),
});
export const AddTripInput = TripFields.extend({
    endDate: z.iso.date().nullable().default(null),
    tripType: TripType.default('leisure'),
    mediaIds: TripFields.shape.mediaIds.default([]),
    shareAsPost: z.boolean().default(false),
    scope: TripFields.shape.scope.default('world'),
    timeZone: HobbyTimeZone.default('UTC'),
}).refine((trip) => !trip.endDate || trip.endDate >= trip.startDate, 'End date must follow the start date');
export const UpdateTripInput = TripFields.partial().extend({ timeZone: HobbyTimeZone.optional() });
export const TripView = z.object({
    id: z.string(),
    countryCode: z.string(),
    countryName: z.string(),
    flag: z.string(),
    subdivisionCodes: z.array(z.string()),
    placeNames: z.array(z.string()),
    startDate: z.iso.date(),
    endDate: z.iso.date().nullable(),
    tripType: TripType,
    media: z.array(TravelMedia).max(20),
    postId: z.string().nullable(),
    scope: TripFields.shape.scope,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const TravelNation = z.object({
    code: z.string(),
    name: z.string(),
    flag: z.string(),
    region: GeoRegion,
    visitedSubdivisions: z.number().int().nonnegative().nullable(),
    totalSubdivisions: z.number().int().positive(),
    percent: z.number().nonnegative().nullable(),
});
export const TravelStats = z.object({
    datasetVersion: z.string(),
    nationsVisited: z.number().int().nonnegative(),
    totalNations: z.number().int().positive(),
    subdivisionsVisited: z.number().int().nonnegative().nullable(),
    totalSubdivisions: z.number().int().positive(),
    worldPercent: z.number().nonnegative().nullable(),
    tripCount: z.number().int().nonnegative(),
    regions: z.array(z.object({
        region: GeoRegion,
        nationsVisited: z.number().int().nonnegative(),
        totalNations: z.number().int().nonnegative(),
        visitedSubdivisions: z.number().int().nonnegative().nullable(),
        totalSubdivisions: z.number().int().nonnegative(),
        percent: z.number().nonnegative().nullable(),
    })),
    nations: z.array(TravelNation),
    updatedAt: z.coerce.date().nullable(),
});
export const BucketType = z.enum(['travel', 'adventure', 'experience', 'skill', 'life_goal']);
export const BucketStatus = z.enum(['planned', 'in_progress', 'achieved']);
export const BucketStep = z.object({
    id: z.string(),
    title: z.string().trim().min(1).max(120),
    done: z.boolean(),
});
export const BucketFields = z.object({
    type: BucketType,
    title: z.string().trim().min(3).max(80),
    why: z.string().trim().max(280),
    targetLabel: z.string().trim().max(80),
    targetMonth: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .nullable(),
    countryCode: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable(),
    visibility: HobbyVisibility,
    remind: z.boolean(),
});
export const AddBucketInput = BucketFields.extend({
    timeZone: HobbyTimeZone.optional(),
    why: BucketFields.shape.why.default(''),
    targetLabel: BucketFields.shape.targetLabel.default(''),
    targetMonth: BucketFields.shape.targetMonth.default(null),
    countryCode: BucketFields.shape.countryCode.default(null),
    visibility: HobbyVisibility.optional(),
    remind: z.boolean().default(false),
    steps: z.array(BucketStep.shape.title).max(10).default([]),
});
export const UpdateBucketInput = BucketFields.partial().extend({
    timeZone: HobbyTimeZone.optional(),
});
export const BucketView = BucketFields.extend({
    id: z.string(),
    status: BucketStatus,
    steps: z.array(BucketStep).max(10),
    achievedAt: z.coerce.date().nullable(),
    achievedViaTripId: z.string().nullable(),
    postId: z.string().nullable(),
    friendsCount: z.number().int().nonnegative().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const BucketCounts = z.object({
    total: z.number().int().nonnegative(),
    achieved: z.number().int().nonnegative(),
});
export const BucketPage = Paginated(BucketView).extend({ counts: BucketCounts });
export const BucketQuery = CursorQuery.extend({
    type: BucketType.optional(),
    status: BucketStatus.optional(),
    q: z.string().trim().max(80).optional(),
});
export const TripQuery = CursorQuery.extend({
    country: z
        .string()
        .regex(/^[A-Z]{2}$/)
        .optional(),
    q: z.string().trim().max(80).optional(),
});
export const TripPage = Paginated(TripView);
export const TravelProfile = z.object({
    stats: TravelStats,
    bucket: z.array(BucketView).max(3),
    bucketCounts: BucketCounts,
    showWorldPercent: z.boolean(),
});
export const TripSaved = z.object({
    trip: TripView,
    statsBefore: TravelStats,
    statsAfter: TravelStats,
    bucketAchieved: z.array(BucketView),
    bucketCounts: BucketCounts,
    postId: z.string().optional(),
    photosAdded: z.number().int().nonnegative(),
});
export const TripDeleted = z.object({
    deleted: z.literal(true),
    stats: TravelStats,
    bucketKeptAchieved: z.boolean(),
});
export const DeleteTripQuery = z.object({
    deletePost: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
export const TravelDeleted = z.object({ deleted: z.literal(true) });
export const TripBucketMatchesQuery = z.object({ countryCode: z.string().regex(/^[A-Z]{2}$/) });
export const TripBucketMatches = z.object({
    items: z.array(z.object({ id: z.string(), title: z.string() })).max(3),
    total: z.number().int().nonnegative(),
});
export const BucketStepInput = z.object({ title: BucketStep.shape.title });
export const UpdateBucketStepInput = BucketStep.pick({ title: true, done: true }).partial();
//# sourceMappingURL=travel.js.map